/* eslint-disable no-console */
import { TMessageListFileType } from '@cian/chats-api';
import { Checkbox, Responsive, UIHeading4, UIText1, UIText2 } from '@cian/ui-kit';
import { IconFileDocument16, IconMediaPhoto16, IconToggleStarOn12 } from '@cian/ui-kit-design-tokens/icons';
import classnames from 'classnames';
import * as React from 'react';

import * as styles from './ChatListItem.css';
import { ChatListItemSwipeButton, EChatListItemSwipeButtonType } from './ChatListItemSwipeButton';
// @ts-expect-error TODO: исправить импорт картинок
import advertImg from './assets/advert.svg';
import { TFormattedCompanionData } from '../../hooks';
import { ERequestApiStatus } from '../../types/appState';
import { TAvailableActions } from '../../types/availableActions';
import { TFileTypes } from '../../types/messageFiles';
import { TOutcomingStatus } from '../../types/messages';
import { ERoute, TRoute } from '../../types/route';
import { useLeftSwipeVisible } from '../../utils/useLeftSwipeVisible';
import { MessageStatusIcon } from '../MessageStatusIcon';

type TChatListItemProps = {
  chatId: string;
  checkboxChecked: boolean;
  checkboxShown: boolean;
  companion: TFormattedCompanionData;
  isActive: boolean;
  fileLastMessage?: { type: TMessageListFileType; name: string };
  isOfferLastMessage?: boolean;
  message: string;
  isMultilineMessage: boolean;
  onClick(): void;
  onSwipeButtonClick(action: EChatListItemSwipeButtonType): void;
  status?: TOutcomingStatus;
  unreadCount: number;
  date: string;
  previewNode: React.ReactNode;
  offerInfo?: string;
  currentRoute: TRoute;
  lastMessageName?: string;
  openedChatRef?: React.RefObject<HTMLDivElement>;
  isStarred: boolean;
  isStarredEnabled: boolean;
  availableActions?: TAvailableActions[];
  chatListStatus: ERequestApiStatus;
};

const CHAT_LIST_FILE_ICON = {
  image: <IconMediaPhoto16 />,
  imageAsDocument: <IconFileDocument16 />,
  document: <IconFileDocument16 />,
};

export const ChatListItem: React.FC<TChatListItemProps> = ({
  chatId,
  checkboxChecked,
  checkboxShown,
  companion = {} as TFormattedCompanionData,
  isActive,
  fileLastMessage,
  message,
  isMultilineMessage,
  isOfferLastMessage,
  onClick,
  onSwipeButtonClick,
  status,
  unreadCount,
  date,
  previewNode,
  offerInfo,
  currentRoute,
  lastMessageName,
  openedChatRef,
  isStarred,
  isStarredEnabled,
  availableActions,
  chatListStatus,
}) => {
  const { companionName, accountTypeName, companionIdStr } = companion;
  const { isLeftSwipeVisible } = useLeftSwipeVisible();
  const itemContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [isItemDisplay, setIsItemDisplay] = React.useState(false);
  const [isItemVisible, setIsItemVisible] = React.useState(false);

  const isMarkAsStarredAvailable = availableActions?.includes('markAsStarred');
  const isUnmarkAsStarredAvailable = availableActions?.includes('unmarkAsStarred');

  const scrollItemToCenter = () => {
    setTimeout(() => {
      if (itemContainerRef.current) {
        itemContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }, 100);
  };

  const onSwipeButtonClickCallback = (type: EChatListItemSwipeButtonType) => {
    onSwipeButtonClick(type);
    scrollItemToCenter();
  };

  React.useEffect(() => {
    if (chatListStatus === ERequestApiStatus.Succeed) {
      setIsItemDisplay(true);
      setTimeout(() => {
        setIsItemVisible(true);
      }, 300);
    }
    if (chatListStatus === ERequestApiStatus.Pending) {
      setIsItemVisible(false);
      setIsItemDisplay(false);
    }
  }, [chatListStatus]);

  return (
    <div
      className={classnames(
        styles['item'],
        isActive && styles['item--active'],
        isItemDisplay ? styles['item-swipe--display'] : styles['item-swipe--no-display'],
        isItemVisible ? styles['item-swipe--visible'] : styles['item-swipe--hidden'],
      )}
      onClick={onClick}
      data-chatid={chatId}
      ref={isActive ? openedChatRef : null}
    >
      {!checkboxShown && (
        <Responsive phone tablet>
          <div
            className={classnames(
              styles['left-swipe-button'],
              /* TODO: функционал непрочитанности пока не готов на сервере, а прочитать можно */
              unreadCount > 0 && isLeftSwipeVisible && styles['left-swipe-button--shown'],
            )}
          >
            <ChatListItemSwipeButton
              type={unreadCount > 0 ? EChatListItemSwipeButtonType.Read : EChatListItemSwipeButtonType.Unread}
              onClick={onSwipeButtonClickCallback}
            />
          </div>
        </Responsive>
      )}

      <div className={styles['item-container']} ref={itemContainerRef}>
        {checkboxShown && (
          <span className={styles['checkbox']}>
            <Checkbox onClick={e => e.preventDefault()} value={chatId} checked={checkboxChecked} />
          </span>
        )}
        <div className={styles['preview-container']}>
          {previewNode}
          {isStarred && isStarredEnabled && currentRoute !== ERoute.Starred && (
            <div className={styles['starred-badge']}>
              <IconToggleStarOn12 color={'icon-main-default'} />
            </div>
          )}
        </div>
        <div className={styles['info']}>
          <div className={styles['common']}>
            <span className={styles['username']}>
              <UIHeading4 whiteSpace="nowrap">
                {companionName || (
                  <>
                    {accountTypeName}&nbsp;<UIText2 color="gray60_100">{`(${companionIdStr})`}</UIText2>
                  </>
                )}
              </UIHeading4>
            </span>
            <UIText2 color="gray60_100" whiteSpace="nowrap">
              {date}
            </UIText2>
            {status && <MessageStatusIcon status={status} />}
          </div>
          <div className={styles['details']}>
            <div className={styles['texts']}>
              {offerInfo && (
                <UIText1>
                  <div className={styles['offer-info']}>{offerInfo}</div>
                </UIText1>
              )}
              <UIText1 color="gray60_100">
                <span className={styles['message']}>
                  {lastMessageName && <span>{lastMessageName}</span>}
                  {fileLastMessage && (
                    <>
                      {fileLastMessage.type
                        ? CHAT_LIST_FILE_ICON[fileLastMessage.type as TFileTypes]
                        : CHAT_LIST_FILE_ICON['document']}
                      <span className={styles['text']}>
                        {fileLastMessage.type === 'image' ? 'Фото' : fileLastMessage.name}
                      </span>
                    </>
                  )}
                  {isOfferLastMessage && (
                    <>
                      <img src={advertImg} width={16} height={16} />
                      <span className={styles['text']}>Объявление</span>
                    </>
                  )}
                  {!fileLastMessage && !isOfferLastMessage && (
                    <span className={classnames(styles['text'], isMultilineMessage && styles['multiline'])}>
                      {message}
                    </span>
                  )}
                </span>
              </UIText1>
            </div>
            {unreadCount !== 0 && (
              <span data-testid="unreadCounter" className={styles['counter']}>
                {unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
      {!checkboxShown && (
        <Responsive phone tablet>
          {currentRoute === ERoute.Spam ? (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Unblock}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          ) : (
            <>
              <div className={classnames(styles['right-swipe-button'])}>
                <ChatListItemSwipeButton
                  type={EChatListItemSwipeButtonType.Delete}
                  onClick={onSwipeButtonClickCallback}
                />
              </div>
              <div className={classnames(styles['right-swipe-button'])}>
                <ChatListItemSwipeButton
                  type={
                    currentRoute === ERoute.Archive
                      ? EChatListItemSwipeButtonType.Return
                      : EChatListItemSwipeButtonType.Archive
                  }
                  onClick={onSwipeButtonClickCallback}
                />
              </div>
            </>
          )}
          {isStarredEnabled && isStarred && isUnmarkAsStarredAvailable && (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Unstarred}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          )}
          {isStarredEnabled && !isStarred && isMarkAsStarredAvailable && (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Starred}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          )}
        </Responsive>
      )}
    </div>
  );
};
